/**
 * Decoration size summary for store products.
 *
 * CustomizeProducts.ElementPlacement holds the placed artwork's box in inches as JSON,
 * e.g. {"width":3.5,"height":1.2,"left":4.25,"top":2}. Only width and height are read.
 */

export interface DecorationSize {
  width: number;
  height: number;
}

export const SIZE_NOT_RECORDED = 'size not recorded';

function toPositiveNumber(value: unknown): number | null {
  if (typeof value === 'number') {
    return isFinite(value) && value > 0 ? value : null;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const n = Number(value.trim());
    return isFinite(n) && n > 0 ? n : null;
  }
  return null;
}

/** Parses ElementPlacement (JSON string or object). Returns null when missing or unusable. */
export function parseDecorationSize(placement: unknown): DecorationSize | null {
  let obj: any = placement;
  if (typeof placement === 'string') {
    if (placement.trim() === '') {
      return null;
    }
    try {
      obj = JSON.parse(placement);
    } catch {
      return null;
    }
  }
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) {
    return null;
  }
  const width = toPositiveNumber(obj.width);
  const height = toPositiveNumber(obj.height);
  return width !== null && height !== null ? { width, height } : null;
}

/** Two decimals at most, trailing zeros trimmed: 3.50 -> "3.5", 2.0 -> "2", 1.234 -> "1.23". */
export function formatInches(value: number): string {
  return (Math.round(value * 100) / 100).toFixed(2).replace(/\.?0+$/, '');
}

/** "3.5 x 1.2 in", or "size not recorded". */
export function formatDecorationSize(placement: unknown): string {
  const size = parseDecorationSize(placement);
  return size ? `${formatInches(size.width)} x ${formatInches(size.height)} in` : SIZE_NOT_RECORDED;
}

/** "<side>: <size>, <decoration name>". Side and decoration parts are dropped when blank. */
export function decorationSizeLine(customizedProduct: any, fallbackDecorationName?: string | null): string {
  const cp = customizedProduct || {};
  const side = String(cp.SideDisplayName || cp.SideName || '').trim();
  const decoration = String(cp.DecorationName || fallbackDecorationName || '').trim();
  let line = formatDecorationSize(cp.ElementPlacement);
  if (side) {
    line = `${side}: ${line}`;
  }
  if (decoration) {
    line = `${line}, ${decoration}`;
  }
  return line;
}

/**
 * One line per side. Uses the CustomizedProducts array when the item groups several sides,
 * otherwise treats the item itself as the single customised product.
 */
export function decorationSizeLines(item: any, fallbackDecorationName?: string | null): string[] {
  if (!item) {
    return [];
  }
  const fallback = fallbackDecorationName || item.DecorationName || item.CustomizationType || null;
  const sides: any[] = Array.isArray(item.CustomizedProducts) && item.CustomizedProducts.length > 0
    ? item.CustomizedProducts
    : [item];
  return sides.map(cp => decorationSizeLine(cp, fallback));
}
